import { api } from "../../../../api";
import { DeliveryDetailsResponse } from "../delivery/types";
import {
  AggregatedPackingPropsResponse,
  DdtProps,
  OrderProps,
  PackingPropsResponse,
  RequestTmsParams,
  TMSProps,
  TMSResponseProp,
  TMSUpdateProps,
  TMSWhsProps,
  WeighingProps,
} from "./types";

export async function getTmsApi(
  id: number,
  params: RequestTmsParams,
): Promise<TMSResponseProp> {
  const { data } = await api.get<TMSResponseProp>(
    `/api/shipping-cases/${id}/loadings`,
    { params },
  );
  return data;
}

export async function getWhsByIdApi(id: number): Promise<TMSWhsProps> {
  const { data } = await api.get<TMSWhsProps>(`/api/loadings-whs/${id}`);
  return data;
}

export async function getTmsWhsApi(
  id: number,
  params: RequestTmsParams,
): Promise<{
  items: TMSWhsProps[] | null;
  page: number;
  size: number;
  total: number;
}> {
  const { data } = await api.get<{
    items: TMSWhsProps[] | null;
    page: number;
    size: number;
    total: number;
  }>(`/api/shipping-cases/${id}/loadings-whs`, { params });
  return data;
}

export async function updateTmsApi(
  id: number,
  params: Partial<TMSUpdateProps>,
  isWhs = false,
): Promise<Partial<TMSProps | TMSWhsProps>> {
  const { data } = await api.patch<Partial<TMSProps | TMSWhsProps>>(
    isWhs ? `/api/loadings-whs/${id}` : `/api/loadings/${id}`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateWhsApi(
  id: number,
  params: Partial<TMSUpdateProps>,
): Promise<Partial<TMSProps>> {
  const { data } = await api.patch<Partial<TMSProps>>(
    `/api/loadings-whs/${id}`,
    {
      ...params,
    },
  );
  return data;
}

export async function getPackingShortApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<PackingPropsResponse> {
  const { data } = await api.get<PackingPropsResponse>(
    `/api/shipping-cases/${id}/packing-lists/short`,
    { params },
  );
  return data;
}

export async function getAggregatedPackingShortApi(
  id: number,
  params: Record<string, any>,
): Promise<AggregatedPackingPropsResponse> {
  const { data } = await api.get<AggregatedPackingPropsResponse>(
    `/api/shipping-cases/${id}/packing-lists/aggregated`,
    { params },
  );
  return data;
}

export async function updatePackingListApi(
  id: number,
  params: {
    packing_list_ids: number[];
    mark?: string;
  },
  isWhs = false,
): Promise<TMSProps> {
  const { data } = await api.put<TMSProps>(
    isWhs
      ? `/api/loadings-whs/${id}/packing-lists`
      : `/api/loadings/${id}/packing-lists`,
    {
      ...params,
    },
  );
  return data;
}

export async function saveFilterDataApi(
  id: number,
  params: Record<string, any>,
): Promise<any> {
  const { data } = await api.post(`/api/loadings/${id}/save-filter`, {
    filter_data: params,
  });
  return data;
}

export async function cancelLoadingOrderApi(id: number): Promise<TMSProps> {
  const { data } = await api.post(`/api/loadings/${id}/cancel`);
  return data;
}

export async function cancelLoadingOrderWhsApi(
  id: number,
): Promise<TMSWhsProps> {
  const { data } = await api.post(`/api/loadings-whs/${id}/cancel`);
  return data;
}

export async function completeLoadingOrderApi(
  id: number,
  isWhs = false,
): Promise<void> {
  await api.post(
    isWhs ? `/api/loadings-whs/${id}/complete` : `/api/loadings/${id}/complete`,
  );
}

export async function reviseLoadingOrderApi(
  id: number,
  data: { packing_list_ids: number[] },
): Promise<void> {
  await api.post(`/api/loadings/${id}/revise`, data);
}

export async function reviseLoadingOrderWhsApi(
  id: number,
  data: { packing_list_ids: number[] },
): Promise<void> {
  await api.post(`/api/loadings-whs/${id}/revise`, data);
}

export async function exportLoadingOrderApi(
  id: number,
  params: OrderProps,
  isWhs = false,
): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.post(
    isWhs
      ? `/api/loadings-whs/${id}/export/pdf`
      : `/api/loadings/${id}/export/pdf`,
    params,
    {
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function exportLoadingDdtApi(
  id: number,
  params: DdtProps,
  isWhs = false,
): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.post(
    isWhs
      ? `/api/loadings-whs/${id}/ddt/export/pdf`
      : `/api/loadings/${id}/ddt/export/pdf`,
    params,
    {
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function exportWeighingSlipApi(
  id: number,
  params: WeighingProps,
  isWhs = false,
): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.post(
    isWhs
      ? `/api/loadings-whs/${id}/weighing/export/pdf`
      : `/api/loadings/${id}/weighing/export/pdf`,
    params,
    {
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function exportTmsApi(
  id: number,
  params?: Partial<RequestTmsParams>,
): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.get(
    `/api/shipping-cases/${id}/loadings-whs/export`,
    {
      params,
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function getDeliveryOrderDetailsApi(
  id: number,
  truck_id: number,
): Promise<DeliveryDetailsResponse> {
  const { data } = await api.get<DeliveryDetailsResponse>(
    `/api/deliveries/${id}/trucks`,
    { params: { id: truck_id } },
  );
  return data;
}
